/**
 * The analysis centre: what was measured, what is wrong, and what to do.
 *
 * It sits above `AnalysisSteps`, which remains the place work is *started*.
 * This component is the place results are *read*, in four layers that answer
 * four different questions:
 *
 *   1. Medya kalitesi        - every subject type. Real measured signals.
 *   2. Konuya özel analiz    - only what this subject actually supports.
 *   3. Rekonstrüksiyon uygunluğu - what the solver will and will not receive.
 *   4. Öneriler              - derived from measured problems, or absent.
 *
 * ## The rules that make it an analysis centre rather than a dashboard
 *
 * **Five status words, and they mean five different things.** `Ölçüldü`,
 * `Sorun bulundu`, `Henüz ölçülmedi`, `Uygulanamaz`, `Desteklenmiyor`. A file
 * nobody has assessed is not a failing file, and a room project has not failed
 * a head analysis it was never given. Nothing here turns absence red.
 *
 * **Usable is not selected.** "46 dosya kullanılabilir" and "reconstruction
 * will use 46 files" are different claims, and this screen never conflates
 * them: the reconstruction layer reports what the *readiness* endpoint says
 * about prepared inputs, separately from what the quality layer says about
 * files.
 *
 * **Recommendations come from measured issue codes, never from a template.**
 * If nothing was measured, there are no recommendations and the section is
 * absent - it is not filled with generic capture advice to look complete.
 *
 * **No internal vocabulary.** `selector`, `purpose=reconstruction`,
 * `frame_root`, engine names and profile ids do not appear. Where the
 * distinction matters - videos are judged on sampled frames, photographs on
 * themselves - it is stated in product terms.
 */

import { useState } from "react";
import { ProblemMediaDialog, type ProblemMedia } from "./ProblemMediaDialog";
import type {
  MediaAsset,
  Project,
  ReconstructionReadiness,
  SessionQualitySummary,
} from "@/api/types";
import { Button, Callout, StatusBadge } from "@/components/ui";
import { Section } from "@/components/product";
import { PROBLEM_STATUSES, isProblem, qualityIssueText } from "./mediaAnalysis";
import { analysisOf, type SessionQuality } from "./useMediaAnalyses";
import "./sessions.css";

/*
 * `qualityIssueText` used to live here and is now in `mediaAnalysis`, where
 * the media cards and the coverage screen can reach it without importing the
 * analysis centre. Re-exported so the one Turkish sentence per issue code has
 * a single definition rather than a copy per screen.
 */
export { qualityIssueText };

/**
 * The five words, and what each one licenses a screen to say.
 *
 * `not_applicable` and `unsupported` are separate on purpose: the first is a
 * property of this project's subject, the second of this deployment. One is
 * answered by choosing a different project type and the other by an operator.
 */
export type MeasurementState =
  | "measured"
  | "problem"
  | "not_measured"
  | "not_applicable"
  | "unsupported";

export const MEASUREMENT_LABELS: Record<MeasurementState, string> = {
  measured: "Ölçüldü",
  problem: "Sorun bulundu",
  not_measured: "Henüz ölçülmedi",
  not_applicable: "Uygulanamaz",
  unsupported: "Desteklenmiyor",
};

/** Only a measured problem is ever cautioned. Absence is neutral, always. */
export const MEASUREMENT_TONES: Record<MeasurementState, "success" | "caution" | "neutral"> = {
  measured: "success",
  problem: "caution",
  not_measured: "neutral",
  not_applicable: "neutral",
  unsupported: "neutral",
};

/** What to do about a measured issue. Only for codes a capture change fixes. */
const ISSUE_ADVICE: Record<string, string> = {
  BLUR: "Kamerayı sabitleyin veya daha yavaş hareket ettirin.",
  POSSIBLE_MOTION_BLUR: "Kamerayı daha yavaş hareket ettirin.",
  UNDEREXPOSED: "Ortam ışığını artırın.",
  OVEREXPOSED: "Doğrudan güçlü ışıktan kaçının.",
  LOW_CONTRAST: "Konu ile arka plan arasında daha belirgin bir ayrım oluşturun.",
  LOW_RESOLUTION: "Daha yüksek çözünürlükte çekim yapın veya konuya yaklaşın.",
  INSUFFICIENT_DETAIL: "Konuya yaklaşın veya daha net görüntü alın.",
};

interface Layer {
  title: string;
  state: MeasurementState;
  detail: string;
}

function LayerRow({ layer }: { layer: Layer }) {
  return (
    <div className="analysis-layer">
      <div className="analysis-layer__head">
        <h3 className="analysis-layer__title">{layer.title}</h3>
        <StatusBadge tone={MEASUREMENT_TONES[layer.state]}>
          {MEASUREMENT_LABELS[layer.state]}
        </StatusBadge>
      </div>
      <p className="analysis-layer__detail">{layer.detail}</p>
    </div>
  );
}

/**
 * The subject-specific layer, and what each subject really supports today.
 *
 * `human_head` has a genuine detector, pose estimation and a five-region
 * coverage model. `human_body` does **not** have a body-region model, and
 * saying "gövde bölgeleri ölçüldü" would be an invention - so it reports the
 * generic measurements and names the gap. Object and spatial projects say the
 * same in their own language.
 */
function subjectLayer(project: Project, headAnalysed: boolean | null): Layer {
  const kind = project.perception_profile?.analysis_kind;
  if (kind === "human_head") {
    // `measured` requires that an analysis actually ran. Describing what the
    // pipeline *would* measure and badging it "Ölçüldü" was exactly the kind
    // of claim this screen exists to avoid - a browser screenshot caught it
    // reading green on a session nobody had analysed.
    return {
      title: "Baş ve yüz analizi",
      state: headAnalysed === true ? "measured" : "not_measured",
      detail:
        headAnalysed === true
          ? "Yüz konumu, baş açısı ve beş bölgeli kapsama ölçüldü. Kimlik tanıma yapılmaz; kimlik bilgisi çıkarılmaz veya saklanmaz."
          : "Yüz konumu, baş açısı ve beş bölgeli kapsama bu proje türü için ölçülebilir; henüz çalıştırılmadı. Kimlik tanıma yapılmaz; kimlik bilgisi çıkarılmaz veya saklanmaz.",
    };
  }
  if (kind === "human_body") {
    return {
      title: "Vücut analizi",
      state: "not_applicable",
      detail:
        "Bu proje türü için gövde bölgesi veya yönelim ölçümü henüz yoktur. Kalite değerlendirmesi ve rekonstrüksiyon akışı olduğu gibi çalışır.",
    };
  }
  if (kind === "object") {
    return {
      title: "Nesne analizi",
      state: "not_applicable",
      detail:
        "Nesneye özel bir dedektör yoktur. Ön/arka gibi semantik yönler atanmaz; değerlendirme genel görüntü kalitesi üzerinden yapılır.",
    };
  }
  return {
    title: "Mekânsal analiz",
    state: "not_applicable",
    detail:
      "Duvar, tavan veya zemin etiketi üretilmez. Değerlendirme genel görüntü kalitesi ve çekim çeşitliliği üzerinden yapılır.",
  };
}

export function AnalysisCenter({
  project,
  media,
  sessionQuality,
  sessionLabel,
  quality,
  readiness,
  headAnalysed,
  onMediaChanged,
}: {
  project: Project;
  media: MediaAsset[];
  /**
   * Grouped counts over the whole session, from the server.
   *
   * `media` is a page. It was also, until now, what these numbers were
   * counted from - so a session of 212 files with 100 loaded reported "100
   * dosya değerlendirildi", and reported it on the screen that decides
   * whether a capture is worth reconstructing. Null while it is loading, or
   * where the caller has no summary to give; the page is the fallback, and
   * the sentences say which one they are describing.
   */
  sessionQuality: SessionQualitySummary | null;
  /** Where these files live, shown on each row of the drill-down. */
  sessionLabel: string;
  /**
   * The session's quality assessments, read once by the screen above.
   *
   * Read there rather than here because the media cards and the analysis
   * chain need the same answers: three components fetching them separately
   * was three requests per file for one set of facts.
   */
  quality: SessionQuality;
  /** What the reconstruction endpoint says, or null while it is unread. */
  readiness: ReconstructionReadiness | null;
  /** Whether the subject analysis has actually run. Null when unknown. */
  headAnalysed: boolean | null;
  /**
   * Re-read the session's media from the server.
   *
   * The drill-down can delete a file, and after that the truth is on the
   * server rather than in this component's props. Splicing the row out
   * locally would leave the counts above it describing a set that no longer
   * exists.
   */
  onMediaChanged: () => void;
}) {
  // Which issue code the reader clicked, or null for "opened the whole list".
  // `undefined` means the drill-down is closed.
  const [drilldown, setDrilldown] = useState<string | null | undefined>(undefined);

  const rows = media.map((asset) => ({ asset, analysis: analysisOf(quality, asset.id) }));
  const assessed = rows.filter((row) => row.analysis.state === "assessed");
  /**
   * Files whose assessment could not be read at all.
   *
   * Counted separately from "not yet assessed" because the two ask for
   * opposite actions: one is fixed by running an analysis, the other by
   * trying again. Folding them together let a failed read tell a user to
   * re-run work that had already been done.
   */
  const unreadable = rows.filter((row) => row.analysis.state === "unknown").length;

  /**
   * The files behind the counts, paired with the assessment that failed them.
   *
   * Matched on the asset id, never on filename or list position: a file the
   * user renamed is still the same row, and an assessment whose asset has
   * left the session simply has no row here.
   */
  const problemMedia: ProblemMedia[] = rows.flatMap(({ asset, analysis }) =>
    analysis.state === "assessed" && isProblem(analysis)
      ? [{ asset, assessment: analysis.assessment }]
      : [],
  );

  // Codes counted across the session, so advice is given once with a number
  // behind it rather than repeated per file.
  const issueCounts = new Map<string, number>();
  for (const { assessment } of problemMedia) {
    for (const issue of assessment.issues ?? []) {
      issueCounts.set(issue.code, (issueCounts.get(issue.code) ?? 0) + 1);
    }
  }

  const videoCount = media.filter((asset) => asset.media_type === "video").length;

  /**
   * The counts the sentences below quote.
   *
   * From the server when it has answered, from the loaded page when it has
   * not. `problemMedia` stays page-local on purpose: it is the list the
   * drill-down opens, and a count is not a list - the server can say that
   * fourteen files have a problem without this component holding fourteen
   * assets to show.
   */
  const totalCount = sessionQuality?.total ?? media.length;
  const assessedCount = sessionQuality?.assessed ?? assessed.length;
  const problemCount = sessionQuality
    ? PROBLEM_STATUSES.reduce(
        (running, status) => running + (sessionQuality.statuses[status] ?? 0),
        0,
      )
    : problemMedia.length;
  /**
   * Said only when the drill-down cannot open everything it just counted.
   *
   * Offering "Sorunlu dosyaları aç (14)" over a list of six is the same lie
   * the page-counted totals were, one screen further along.
   */
  const beyondThePage = Math.max(0, problemCount - problemMedia.length);

  const measured = "Netlik, pozlama, kontrast ve çözünürlük ölçülür.";
  /**
   * Files whose verdict could not be read are always named, in every branch.
   *
   * Silence about them is what turns a partial read into a confident summary
   * of a session nobody fully measured.
   */
  const unreadableNote =
    unreadable > 0 ? ` ${unreadable} dosyanın analiz durumu okunamadı.` : "";

  const qualityLayer: Layer = ((): Layer => {
    const title = "Medya kalitesi";
    if (totalCount === 0) {
      return { title, state: "not_measured", detail: "Bu oturumda henüz dosya yok." };
    }
    if (quality.loading) {
      return { title, state: "not_measured", detail: "Kalite ölçümleri okunuyor." };
    }
    if (assessedCount === 0) {
      return {
        title,
        state: "not_measured",
        detail:
          unreadable === media.length
            ? "Kalite ölçümleri okunamadı. Sayfayı yenileyip tekrar deneyin."
            : `${totalCount - unreadable} dosya henüz değerlendirilmedi.${unreadableNote}`,
      };
    }
    if (problemCount > 0) {
      return {
        title,
        state: "problem",
        detail: `${assessedCount} dosya değerlendirildi; ${problemCount} tanesinde kalite sorunu bulundu. ${measured}${unreadableNote}`,
      };
    }
    return {
      title,
      state: "measured",
      detail: `${assessedCount} dosya değerlendirildi; kalite sorunu bulunmadı. ${measured}${unreadableNote}`,
    };
  })();

  const suitabilityLayer: Layer = readiness
    ? {
        title: "Rekonstrüksiyon uygunluğu",
        state: readiness.can_start
          ? "measured"
          : readiness.unprepared_videos.length > 0
            ? "problem"
            : "not_measured",
        detail: readiness.can_start
          ? `Şu anda ${readiness.usable_frame_count} görüntü rekonstrüksiyona girebilir.`
          : readiness.unprepared_videos.length > 0
            ? `${readiness.unprepared_videos.length} video henüz rekonstrüksiyon için hazırlanmadı; hazırlanmadan görüntü olarak sayılmazlar.`
            : `Rekonstrüksiyon için en az ${readiness.minimum_required} görüntü gerekir; şu anda ${readiness.usable_frame_count} var.`,
      }
    : {
        title: "Rekonstrüksiyon uygunluğu",
        state: "not_measured",
        detail: "Uygunluk durumu şu anda okunamadı.",
      };

  return (
    <Section title="Analiz merkezi">
      <div className="analysis-center">
        <LayerRow layer={qualityLayer} />
        <LayerRow layer={subjectLayer(project, headAnalysed)} />
        <LayerRow layer={suitabilityLayer} />
      </div>

      {/*
        The way into the files, independent of the advice list below.

        The advice list is built from measured *issue codes*, and a file can
        be graded `poor` with no issue record at all - which is how a session
        reading "4 sorunlu" offered exactly one file to look at. The count
        here is the same number the quality layer states, and it opens all of
        them.
      */}
      {(problemMedia.length > 0 || beyondThePage > 0) && (
        <div className="row">
          {problemMedia.length > 0 && (
            <Button variant="secondary" onClick={() => setDrilldown(null)}>
              Sorunlu dosyaları aç ({problemMedia.length})
            </Button>
          )}
          {/*
            Said even when there is no button to sit beside it. The server can
            report problems in files this page has not loaded, and a count with
            no way to reach any of it - and no word about why - reads as a
            broken screen rather than a partial one.
          */}
          {beyondThePage > 0 && (
            <span className="faint">
              {beyondThePage} sorunlu dosya henüz yüklenmedi; listeyi genişletince
              görünürler.
            </span>
          )}
        </div>
      )}

      {/*
        Videos are judged on sampled frames and photographs on themselves.
        Stated because the two are not the same measurement, and a screen that
        implied they were would be describing work the system did not do.
      */}
      {videoCount > 0 && (
        <Callout tone="neutral">
          Videolar, seçilen kareler üzerinden değerlendirilir; fotoğraflar doğrudan
          değerlendirilir. İki ölçüm aynı şey değildir.
        </Callout>
      )}

      {/*
        Present only when something was measured. No measured issue means no
        section, rather than a box of generic advice that would appear
        identically for a perfect session.
      */}
      {issueCounts.size > 0 && (
        <div className="analysis-advice">
          <h3 className="analysis-advice__title">Çekim önerileri</h3>
          <ul className="analysis-advice__list">
            {Array.from(issueCounts.entries())
              .sort((a, b) => b[1] - a[1])
              .map(([code, count]) => (
                <li key={code}>
                  {/*
                    The count is the control. It is a claim about specific
                    files, so it opens them - a number the reader cannot
                    inspect is one they have to take on faith.
                  */}
                  <button
                    type="button"
                    className="analysis-advice__count"
                    onClick={() => setDrilldown(code)}
                  >
                    {count} dosyada: {qualityIssueText(code)}
                  </button>{" "}
                  {ISSUE_ADVICE[code] ?? "Bu dosyaları yeniden çekmek sonucu iyileştirebilir."}
                </li>
              ))}
          </ul>
        </div>
      )}

      <p className="faint">
        Bu bölüm ölçülen sonuçları gösterir. Analizleri aşağıdaki adımlardan
        çalıştırabilirsiniz.
      </p>

      {drilldown !== undefined && problemMedia.length > 0 && (
        <ProblemMediaDialog
          rows={problemMedia}
          sessionLabel={sessionLabel}
          projectId={project.id}
          initialCode={drilldown}
          onClose={() => setDrilldown(undefined)}
          onChanged={onMediaChanged}
        />
      )}
    </Section>
  );
}
